// src/pages/SummaryPage.tsx

import React, { useState, useEffect } from 'react';
import { scoringApi } from '../api';
import type { MonthlyScore, MonthSummary, Grade } from '../types';

const GRADE_COLOR: Record<Grade, string> = {
  A: 'var(--green)', B: '#7dd3fc', C: 'var(--orange)', D: 'var(--red)',
};

function gradeColor(g: Grade) { return GRADE_COLOR[g] || 'var(--text2)'; }

function currentYearMonth(): string {
  const now = new Date(Date.now() + 7 * 3600000); // Jakarta UTC+7
  return now.toISOString().slice(0, 7);
}

function monthLabel(ym: string): string {
  const [y, m] = ym.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

// ── STACKED BAR ───────────────────────────────────────────────────

function StackedBar({ done, missed, label, warn }: {
  done: number; missed: number; label: string; warn: boolean;
}) {
  const pctColor = done >= 80 ? 'var(--green)' : done >= 65 ? 'var(--orange)' : 'var(--red)';
  return (
    <div style={{
      display:'flex', alignItems:'center', gap:12,
      padding: warn ? '4px 8px' : '4px 0',
      background: warn ? 'rgba(255,94,125,0.04)' : 'transparent',
      borderRadius: 6, margin: warn ? '0 -8px' : 0,
    }}>
      <div style={{ fontSize:11, color: warn ? 'var(--red)' : 'var(--text2)',
        width:'clamp(100px, 30%, 180px)', flexShrink:0, textAlign:'right', fontFamily:'var(--font-mono)' }}>
        {warn ? `⚠ ${label}` : label}
      </div>
      <div style={{ flex:1, height:18, borderRadius:4, overflow:'hidden',
        background:'var(--bg3)', border:'1px solid var(--border)', position:'relative' }}>
        <div style={{ position:'absolute', left:0, top:0, bottom:0,
          width:`${done}%`, background:`${pctColor}`, opacity:0.75 }} />
        <div style={{ position:'absolute', right:0, top:0, bottom:0,
          width:`${missed}%`, background:'var(--red)', opacity:0.35 }} />
        <div style={{ position:'absolute', inset:0, display:'flex',
          alignItems:'center', justifyContent:'space-between', padding:'0 8px',
          fontSize:9, fontFamily:'var(--font-mono)', pointerEvents:'none' }}>
          <span style={{ color:'rgba(255,255,255,0.85)', fontWeight:600 }}>{done}%</span>
          <span style={{ color:'rgba(255,150,150,0.8)' }}>{missed}%</span>
        </div>
      </div>
      <div style={{ fontSize:11, fontWeight:600, fontFamily:'var(--font-mono)',
        color: pctColor, width:36 }}>
        {done}%
      </div>
    </div>
  );
}

// ── MOM CHART ─────────────────────────────────────────────────────

function MoMChart({ months }: { months: MonthSummary[] }) {
  const monthMap: Record<string, number> = {};
  months.forEach(m => { monthMap[m.yearMonth] = m.buildScore; });

  const allMonths = Array.from({ length: 12 }, (_, i) => {
    const y = new Date().getFullYear();
    const m = String(i + 1).padStart(2, '0');
    return `${y}-${m}`;
  });

  const current = currentYearMonth();

  return (
    <div style={{ display:'flex', gap:8, alignItems:'flex-end', height:80 }}>
      {allMonths.map(ym => {
        const val      = monthMap[ym];
        const isCurrent = ym === current;
        const hasPast   = val !== undefined;
        const barH      = hasPast ? `${Math.max(val, 4)}%` : '4px';
        const barBg     = isCurrent ? 'var(--green)' : hasPast ? 'rgba(0,229,160,0.35)' : 'var(--bg3)';

        return (
          <div key={ym} style={{ flex:1, display:'flex', flexDirection:'column',
            alignItems:'center', gap:4 }}>
            <div style={{ fontSize:9, fontFamily:'var(--font-mono)',
              color: hasPast ? (isCurrent ? 'var(--green)' : 'var(--text3)') : 'var(--text3)',
              fontWeight: isCurrent ? 700 : 400 }}>
              {hasPast ? `${val}%` : ''}
            </div>
            <div style={{ width:'100%', position:'relative', height:60,
              background:'var(--bg3)', borderRadius:4, overflow:'hidden',
              border:'1px solid var(--border)' }}>
              {/* 80% threshold line */}
              <div style={{ position:'absolute', left:0, right:0, bottom:'80%',
                borderTop:'1px dashed rgba(255,181,71,0.4)', zIndex:1 }} />
              <div style={{
                position:'absolute', bottom:0, left:0, right:0,
                height: hasPast ? `${val}%` : '2px',
                background: barBg,
                boxShadow: isCurrent ? '0 0 10px rgba(0,229,160,0.3)' : 'none',
                transition:'height 0.6s ease',
              }} />
            </div>
            <div style={{ fontSize:8, fontFamily:'var(--font-mono)',
              color: isCurrent ? 'var(--green)' : 'var(--text3)',
              fontWeight: isCurrent ? 700 : 400 }}>
              {ym.slice(5, 7) === '01' ? ym.slice(2,4) : ''}{
                ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][Number(ym.slice(5,7))]
              }
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── MAIN PAGE ─────────────────────────────────────────────────────

export default function SummaryPage() {
  const [month, setMonth]           = useState(currentYearMonth());
  const [data, setData]             = useState<MonthlyScore | null>(null);
  const [momData, setMomData]       = useState<MonthSummary[]>([]);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [monthly, summary] = await Promise.all([
          scoringApi.getMonthly(month),
          scoringApi.getSummary(),
        ]);
        setData(monthly);
        setMomData(summary.months);
      } catch {}
      setLoading(false);
    };
    load();
  }, [month]);

  const prevMonth = () => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 2, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);
  };
  const nextMonth = () => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m, 1);
    const cur = currentYearMonth();
    const next = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
    if (next <= cur) setMonth(next);
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom:28 }}>
        <div style={{ fontSize:26, fontWeight:600, letterSpacing:-0.5 }}>Monthly Summary</div>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginTop:14 }}>
          <button onClick={prevMonth} style={navBtn}>← Prev</button>
          <div style={{
            padding:'6px 16px', borderRadius:'var(--radius-sm)', fontSize:13,
            background:'var(--green-dim)', border:'1px solid rgba(0,229,160,0.2)',
            color:'var(--green)', fontWeight:600, fontFamily:'var(--font-mono)',
          }}>
            {monthLabel(month)}
          </div>
          <button onClick={nextMonth} disabled={month === currentYearMonth()}
            style={{ ...navBtn, opacity: month === currentYearMonth() ? 0.3 : 1 }}>
            Next →
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ color:'var(--text3)', fontSize:12, fontFamily:'var(--font-mono)' }}>calculating scores...</div>
      ) : !data ? (
        <div className="card" style={{ textAlign:'center', padding:40 }}>
          <div style={{ fontSize:14, color:'var(--text2)' }}>No data for this month yet.</div>
        </div>
      ) : (
        <>
          {/* Score cards */}
          <div style={{ display:'grid', gridTemplateColumns: data.overallBreak ? 'repeat(auto-fit, minmax(140px, 1fr))' : '1fr', gap:12, marginBottom:16 }}>
            <ScoreCard label="Build Score" pct={data.overallBuild.pct} grade={data.overallBuild.grade} />
            {data.overallBreak && (
              <ScoreCard label="Break Score" pct={data.overallBreak.pct} grade={data.overallBreak.grade} />
            )}
          </div>

          {/* Achievement stacked bars */}
          <div className="card" style={{ marginBottom:16 }}>
            <div style={{ fontSize:10, fontWeight:700, color:'var(--text2)', letterSpacing:'0.5px',
              textTransform:'uppercase', marginBottom:14, fontFamily:'var(--font-mono)' }}>
              Achievement Rate · % of applicable periods hit
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {data.habitResults.map(h => (
                <StackedBar
                  key={h.habitId}
                  label={h.habitName}
                  done={h.pct}
                  missed={100 - h.pct}
                  warn={h.needsWork}
                />
              ))}
            </div>
            <div style={{ display:'flex', gap:14, marginTop:14, flexWrap:'wrap' }}>
              <LegendDot color="var(--green)" label="Done" />
              <LegendDot color="var(--red)" label="Missed" opacity={0.35} />
              <div style={{ marginLeft:'auto', fontSize:10, color:'var(--orange)',
                fontFamily:'var(--font-mono)' }}>⚠ below 80% = needs work</div>
            </div>
          </div>

          {/* MoM Trend */}
          <div className="card" style={{ marginBottom:16 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
              <div>
                <div style={{ fontSize:10, fontWeight:700, color:'var(--text2)', letterSpacing:'0.5px',
                  textTransform:'uppercase', fontFamily:'var(--font-mono)' }}>
                  Month-over-Month · Build score
                </div>
              </div>
              <div style={{ fontSize:9, color:'var(--orange)', fontFamily:'var(--font-mono)' }}>
                — 80% target
              </div>
            </div>
            <MoMChart months={momData} />
          </div>
        </>
      )}
    </div>
  );
}

const navBtn: React.CSSProperties = {
  padding:'6px 12px', borderRadius:'var(--radius-sm)', fontSize:11,
  background:'var(--bg3)', border:'1px solid var(--border)', color:'var(--text2)', cursor:'pointer',
};

function ScoreCard({ label, pct, grade }: { label: string; pct: number; grade: Grade }) {
  return (
    <div className="card">
      <div style={{ fontSize:9, fontWeight:700, color:'var(--text3)', letterSpacing:'1px',
        textTransform:'uppercase', fontFamily:'var(--font-mono)', marginBottom:8 }}>
        {label}
      </div>
      <div style={{ display:'flex', alignItems:'baseline', gap:10 }}>
        <span style={{ fontSize:40, fontWeight:700, fontFamily:'var(--font-mono)',
          color: gradeColor(grade), lineHeight:1 }}>
          {grade}
        </span>
        <span style={{ fontSize:20, color:'var(--text2)', fontFamily:'var(--font-mono)' }}>
          {pct}%
        </span>
      </div>
    </div>
  );
}

function LegendDot({ color, label, opacity = 0.85 }: {
  color: string; label: string; opacity?: number;
}) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:5 }}>
      <div style={{ width:10, height:10, borderRadius:2, background:color, opacity }} />
      <span style={{ fontSize:10, color:'var(--text2)', fontFamily:'var(--font-mono)' }}>{label}</span>
    </div>
  );
}
